import { useCallback, useState, type ComponentType, type ReactNode } from 'react';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import AiAnalysisPdfDownloadMenu from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisPdfDownloadMenu';
import AiInsufficientBalanceModal, {
  AI_WALLET_INSUFFICIENT_CODE
} from '@/app/common/components/AiInsufficientBalanceModal';
import aiContentToolsApi, {
  useGetAiDetectionDetailsQuery,
  useRunAiDetectionMutation
} from '@/app/ContentTools/api/aiContentAnalysisApi';
import AiDetectorScoreCard from '@/app/ContentTools/components/ai-content-analysis/AiDetectorScoreCard';
import { AiAnalysisReportSkeleton } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSkeletons';
import mapAiDetectionData from '@/app/ContentTools/utils/mapAiDetectionData';
import type { AiDetectionDetailsData } from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import { useAiDetectionSocket } from '@/app/ContentTools/hooks/useAiDetectionSocket';
import { getAiWarningMessage } from '@/app/common/validators/aiMessages';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';

type FalconCardHeaderProps = {
  className?: string;
  light?: boolean;
  title: ReactNode;
  endEl?: ReactNode;
};

type ApiErrorResponse = {
  status?: number;
  data?: {
    code?: number;
    msg?: string;
  };
};

type AiDetectorDetailsPanelProps = {
  analysisId: string;
  domainSlug: string;
  keyword?: string;
  markdownReport: ComponentType<{ message: string }>;
};

const TypedFalconCardHeader =
  FalconCardHeader as ComponentType<FalconCardHeaderProps>;

const AiDetectorDetailsPanel = ({
  analysisId,
  domainSlug,
  keyword,
  markdownReport: MarkdownReport
}: AiDetectorDetailsPanelProps) => {
  const dispatch = useDispatch();
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] =
    useState(false);
  const [generatedData, setGeneratedData] =
    useState<AiDetectionDetailsData | null>(null);

  const { data: detailsResponse, isLoading, error, isError } =
    useGetAiDetectionDetailsQuery({ id: analysisId }, { skip: !analysisId });
  const [runAiDetection, { isLoading: isRunning }] = useRunAiDetectionMutation();

  const detailsData = detailsResponse?.data;
  const detectionStatus = detailsData?.detection_status;
  const isDetectionPending =
    detectionStatus != null && detectionStatus !== 'finished';

  const revalidateDetectionApis = useCallback(() => {
    dispatch(
      aiContentToolsApi.util.invalidateTags([
        { type: 'AiContentOverview', id: analysisId },
        { type: 'AiDetectionDetails', id: analysisId }
      ])
    );
  }, [analysisId, dispatch]);

  useAiDetectionSocket({
    historyId: analysisId,
    active: Boolean(analysisId && isDetectionPending),
    onFinished: revalidateDetectionApis
  });

  const errorData = (error as ApiErrorResponse | undefined)?.data;
  const isNotFound =
    detailsResponse?.code === 400 ||
    detailsResponse?.msg === 'آیتم موردنظر پیدا نشد.' ||
    (isError &&
      ((error as ApiErrorResponse)?.status === 400 ||
        errorData?.code === 400 ||
        errorData?.msg === 'آیتم موردنظر پیدا نشد.'));
  const mappedDetails = mapAiDetectionData(detailsResponse?.data);
  const mappedGenerated = mapAiDetectionData(generatedData);
  const detector = mappedGenerated || mappedDetails;
  const isDetectionMissing =
    !isDetectionPending &&
    detailsResponse?.code === 200 &&
    detailsData?.ai_detection == null &&
    detailsData?.ai_detection_report == null;

  const handleRunDetection = async () => {
    try {
      const res = await runAiDetection({
        id: analysisId,
        domain_slug: domainSlug
      }).unwrap();
      if (res?.code === 200 && res.data) {
        successMessage('تشخیص AI با موفقیت انجام شد');
        setGeneratedData(res.data);
        return;
      }
      if (res?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      const warningText = getAiWarningMessage(res?.code);
      if (warningText) {
        warningMessage(warningText);
        return;
      }
      errorMessage(res?.msg || 'در انجام تشخیص AI خطایی رخ داد.');
    } catch (err) {
      const error = err as ApiErrorResponse;
      if (error?.data?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      const warningText = getAiWarningMessage(error?.data?.code);
      if (warningText) {
        warningMessage(warningText);
        return;
      }
      errorMessage(
        error?.data?.msg || 'در انجام تشخیص AI خطایی رخ داد. لطفا دوباره تلاش کنید.'
      );
    }
  };

  if (isLoading) {
    return <AiAnalysisReportSkeleton />;
  }

  if (!detector && isDetectionPending) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner
            animation="border"
            variant="primary"
            className="mb-3"
            style={{ width: 28, height: 28 }}
          />
          <p className="fw-semi-bold mb-1">در حال انجام تشخیص AI...</p>
          <p className="text-600 mb-0 fs--1">
            به‌محض آماده‌شدن نتیجه، این صفحه به‌روز می‌شود.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (!detector && isDetectionMissing) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="fw-semi-bold mb-0">برای این مورد تشخیص AI یافت نشد.</p>
        </Card.Body>
      </Card>
    );
  }

  if (!detector && isNotFound) {
    return (
      <>
        <Card>
          <Card.Body className="text-center py-5">
            <p className="fw-semi-bold mb-2">موردی یافت نشد.</p>
            <p className="text-600 mb-3">
              می‌خواهید برای این مورد هم تشخیص AI انجام دهید؟
            </p>
            <Button
              variant="falcon-primary"
              size="sm"
              onClick={handleRunDetection}
              disabled={isRunning}
            >
              {isRunning ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-1"
                    style={{ width: 16, height: 16 }}
                  />
                  در حال انجام تشخیص AI...
                </>
              ) : (
                'انجام تشخیص AI'
              )}
            </Button>
          </Card.Body>
        </Card>
        <AiInsufficientBalanceModal
          show={showInsufficientBalanceModal}
          onHide={() => setShowInsufficientBalanceModal(false)}
        />
      </>
    );
  }

  if (!detector) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-600 mb-0">
            {detailsResponse?.msg && detailsResponse.code !== 200
              ? detailsResponse.msg
              : 'در دریافت جزئیات تشخیص AI خطایی رخ داد.'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col xs={12} lg={8} className="order-1 order-lg-0">
        <Card className="w-100">
          <TypedFalconCardHeader
            className="fs--1"
            light
            title={
              <span className="text-muted fw-bold fs-0">
                {detector.keyword || keyword || ''}
              </span>
            }
            endEl={
              <AiAnalysisPdfDownloadMenu
                id={analysisId}
                currentType="ai_detection"
                keyword={detector.keyword || keyword}
              />
            }
          />
          <Card.Body>
            <MarkdownReport message={detector.message} />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={4} className="order-0 order-lg-1">
        <div
          className="sticky-top"
          style={{
            top: '1rem',
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
          }}
        >
          <AiDetectorScoreCard data={detector} />
        </div>
      </Col>
    </Row>
  );
};

export default AiDetectorDetailsPanel;
